/* eslint-disable functional/immutable-data */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import React, { useState } from 'react'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
  type GridValueFormatter,
  gridClasses,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import {
  getDispatchGroupActionsLogs as getDispatchGroupReportSearchLogs,
  getDispatchGroupLogDetails,
} from '../services'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { mapActionType } from './reportTypeOptions'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AuditLogDetailsModal from '../../AuditLogDetailsModal'
import AuditDispatchGroupReportFilters from './AuditDispatchGroupReportFilters'
import { DispatchGroupReportSearchLog, DispatchGroupReportSearchLogRow, SearchLogResponse } from '../interfaces'


// Função extraída para mapear os dados da API para o formato de linha da tabela
// Separa a responsabilidade de transformação de dados
const mapSearchLogToRow = (
  element: DispatchGroupReportSearchLog,
  index: number,
  page: number,
  pageSize: number,
  translate: (key: string) => string
): DispatchGroupReportSearchLogRow => {
  const generatedId = element.id ?? page * pageSize + index + 1

  const reportName = element.route
    ? element.route
    : translate('Unknown Report')

  // action_display agora é derivado apenas da rota (reportName)
  const actionLabel = reportName

  const extractDatesFromContent = (content: string) => {
    try {
      const parsedContent = JSON.parse(content)
      return {
        start_date: parsedContent.start_date || null,
        finish_date: parsedContent.finish_date || null,
      }
    } catch {
      return {
        start_date: null,
        finish_date: null,
      }
    }
  }

  const {
    start_date: contentStartDate,
    finish_date: contentFinishDate,
  } = element.content
    ? extractDatesFromContent(element.content)
    : { start_date: null, finish_date: null }

  return {
    id: generatedId,
    ...element,
    'user:representation_name': element?.user_name || '',
    report_display: reportName,
    action_display: actionLabel,
    origin: element?.station
      ? `Estação - ${element?.station}`
      : element?.DispatchGroup
      ? `DispatchGroup - ${element?.DispatchGroup}`
      : '',
    content_start_date: contentStartDate,
    content_finish_date: contentFinishDate,
    content_period:
      contentStartDate || contentFinishDate
        ? `${
            contentStartDate
              ? dayjs(contentStartDate).format('DD/MM/YYYY HH:mm')
              : ''
          }${contentStartDate && contentFinishDate ? ' - ' : ''}${
            contentFinishDate
              ? dayjs(contentFinishDate).format('DD/MM/YYYY HH:mm')
              : ''
          }`
        : null,
  } as DispatchGroupReportSearchLogRow
}



export default function ListAllAuditDispatchGroupReport() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const action = useHookstate<string | null>(null)
  const loadingButton = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const userId = useHookstate<number | null>(null)
  const resetValues = useHookstate(false)
  const focusChange = useHookstate<boolean>(false)
  const initialPeriod = useHookstate<string>('')
  const originType = useHookstate<'DispatchGroup' | 'station' | null>(null)
  const selectedDispatchGroup = useHookstate<number | null>(null)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)
  const limit = 15
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [rowCount, setRowCount] = React.useState(0)
  const [paginationModel, setPaginationModel] = React.useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ])
  const tableMode = 'server'
  const expandDispatchGroupReports = useHookstate<boolean>(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] =
    useState<DispatchGroupReportSearchLogRow | null>(null)
  const [loadingRowId, setLoadingRowId] = useState<number | null>(null) // Novo state para loading do row específico
  const [openTooltips, setOpenTooltips] = useState<Record<number, boolean>>({})


const columns: readonly GridColDef[] = [
  {
    field: 'user:representation_name',
    headerName: translate('User'),
    flex: 1,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: 'request_type',
    headerName: translate('action'),
    flex: 1,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    valueGetter: (value: string) => { // Tipado explicitamente como string para remover any implícito
      // passa um único objeto requestType { type: ... }
      return mapActionType(translate, { type: value })
    },
  },
  {
    field: 'created_at',
    headerName: translate('Performed at'),
    type: 'date',
    flex: 1,
    filterable: false,
    sortable: true,
    disableColumnMenu: false,
    valueFormatter: (value) =>
      dayjs(value as string).format('DD/MM/YYYY HH:mm'),
  },
  {
    field: 'actions',
    headerName: '',
    sortable: false,
    filterable: false,
    align: 'center',
    width: 80,
    renderCell: (params) => (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Tooltip
          title={translate('Visualizar')}
          open={openTooltips[String((params.row as DispatchGroupReportSearchLogRow).id)] ?? false}
          onOpen={() =>
            setOpenTooltips(prev => ({
              ...prev,
              [String((params.row as DispatchGroupReportSearchLogRow).id)]: true,
            }))
          }
          onClose={() =>
            setOpenTooltips(prev => ({
              ...prev,
              [String((params.row as DispatchGroupReportSearchLogRow).id)]: false,
            }))
          }
        >
          <IconButton
            onClick={() => {
              setOpenTooltips(prev => ({
                ...prev,
                [String((params.row as DispatchGroupReportSearchLogRow).id)]: false,
              })) // Força o fechamento do tooltip ao clicar
              handleView(params.row as DispatchGroupReportSearchLogRow)
            }}
            size="small"
            aria-label={translate('Visualizar')}
            disabled={loadingRowId === (params.row as DispatchGroupReportSearchLogRow).id}
          >
            {loadingRowId === (params.row as DispatchGroupReportSearchLogRow).id ? (
              <CircularProgress size={20} />
            ) : (
              <VisibilityIcon />
            )}
          </IconButton>
        </Tooltip>
      </div>
    ),
  },
]
  // Renomeada para applyFilters, sem any, e tipada adequadamente
  const applyFilters = async (
    model = paginationModel,
    currentSortModel: GridSortModel = sortModel
  ): Promise<boolean> => {
    if (!startDate || !finishDate) {
      notify({
        type: 'error',
        message: translate('Start date and end date must be provided'),
      })
      return false
    }

    loadingButton.set(true)

    const page = model.page
    const pageSize = model.pageSize

    const orders = currentSortModel.map(({ field, sort }) => ({
      field,
      sort: sort || 'desc',
    }))

    const searchLogData: SearchLogResponse =
      await getDispatchGroupReportSearchLogs({
        user_id: userId.get() || undefined,
        start_date: startDate,
        finish_date: finishDate,
        action: action.get(),
        page: page,
        limit: pageSize,
        orders:
          orders.length > 0 ? orders : [{ field: 'created_at', sort: 'desc' }],
      }).finally(() => {
        loadingButton.set(false)
        expandDispatchGroupReports.set(true)
      })

    // Usa a função extraída para mapear os dados, separando responsabilidades
    setRows(
      searchLogData.data.map((element, index) =>
        mapSearchLogToRow(element, index, page, pageSize, translate)
      )
    )

    setRowCount(searchLogData.rowsCount)
    return true
  }

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    userId.set(null)
    action.set(null)
    setRows([])
    setSortModel([{ field: 'created_at', sort: 'desc' }])
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    expandDispatchGroupReports.set(false)
  }

  const searchFromFirstPage = () => {
    const firstPageModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    applyFilters(firstPageModel, sortModel) // Chamada atualizada para a nova função
  }

  const handleView = async (row: DispatchGroupReportSearchLogRow) => {
    setLoadingRowId(row.id ?? null)
    try {
      if (row.id == null) {
        notify({
          type: 'error',
          message: translate('Invalid log id'),
        })
        return
      }

      const details = await getDispatchGroupLogDetails(Number(row.id))
      setSelectedRow(details as DispatchGroupReportSearchLogRow)
      setOpenModal(true)
    } catch (error) {
      notify({
        type: 'error',
        message: translate('Erro ao carregar detalhes do log'),
      })
    } finally {
      setLoadingRowId(null)
    }
  }

  // Helpers para extrair deleted_dispatch_group de selectedRow.content
  const parseContent = (content: unknown) => {
    try {
      return typeof content === 'string' ? JSON.parse(content) : content
    } catch {
      return null
    }
  }

  const getDeletedDispatchGroupFromRow = (row: DispatchGroupReportSearchLogRow | null) => {
    if (!row) return null
    const content = parseContent((row as any)?.content)
    return (content && (content.deleted_dispatch_group ?? content.deletedDispatchGroup)) ?? (row as any)?.deleted_dispatch_group ?? null
  }

  const modalFields = React.useMemo(() => {
    if (!selectedRow) return []

    const requestType = String((selectedRow as any)?.request_type ?? (selectedRow as any)?.requestType ?? '').toUpperCase()
    const formatDate = (date?: string | null) =>
      date ? dayjs(date).format('DD/MM/YYYY HH:mm') : 'Não informado'

    if (requestType === 'DELETE') {
      const deleted = getDeletedDispatchGroupFromRow(selectedRow)

      return [
        { label: 'Nome', value: deleted?.name ?? 'Não informado' },
        { label: 'Agência', value: deleted?.agency?.name ?? 'Não informado' },
        { label: 'Agência (acrônimo)', value: deleted?.agency?.acronym ?? 'Não informado' },
        { label: 'Sigla', value: deleted?.acronym ?? 'Não informado' },
        {
          label: 'Não operacional',
          value:
            typeof (deleted?.is_non_operational) !== 'undefined'
              ? deleted?.is_non_operational ? 'Sim' : 'Não'
              : 'Não informado',
        },
        {
          label: 'Área',
          value: deleted?.areas ? (deleted.areas as readonly { readonly name: string }[]).map((a) => a.name).join(', ') : 'Não informado',
        },
        { label: 'Realizado em', value: formatDate(selectedRow?.created_at) },
      ]
    }

    // Caso padrão para outras ações
    return [
      { label: 'Nome', value: selectedRow?.name ?? 'Não informado' },
      { label: 'Agência', value: selectedRow?.agency?.name ?? 'Não informado' },
      { label: 'Agência (acrônimo)', value: selectedRow?.agency?.acronym ?? 'Não informado' },
      { label: 'Sigla', value: selectedRow?.acronym ?? 'Não informado' },
      {
        label: 'Não operacional',
        value:
          typeof selectedRow?.is_non_operational !== 'undefined'
            ? selectedRow?.is_non_operational ? 'Sim' : 'Não'
            : 'Não informado',
      },
      {
        label: 'Área',
        value: selectedRow?.areas ? selectedRow.areas.map((a) => a.name).join(', ') : 'Não informado',
      },
      { label: 'Realizado em', value: formatDate(selectedRow?.created_at) },
    ]
  }, [selectedRow])

  return (
    <>
      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <AuditDispatchGroupReportFilters
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          focusChange={focusChange}
          resetValues={resetValues}
          userId={userId}
          initialPeriod={initialPeriod}
          action={action}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={() => clearFilters()}
          searchButton={() => searchFromFirstPage()}
          originType={originType}
          selectedDevice={selectedDispatchGroup}
        />
      </InternalContent>
      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() =>
                expandDispatchGroupReports.set(
                  !expandDispatchGroupReports.get()
                )
              }
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandDispatchGroupReports.value ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandDispatchGroupReports.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationMode={tableMode}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
              applyFilters(model, sortModel)
            }}
            sortingMode={tableMode}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model)
              const firstPageModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
              }
              setPaginationModel(firstPageModel)
              applyFilters(firstPageModel, model)
            }}
            columnVisibilityModel={{
              id: false,
            }}
            disableColumnFilter
            disableColumnSelector
            autoHeight
            pagination
            filterMode={tableMode}
            rowCount={rowCount}
            getRowId={(row) => Number(row.id)}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>
      <AuditLogDetailsModal
        open={openModal}
        onClose={(event?: unknown, reason?: string) => {
          if (reason !== 'backdropClick') {
            setOpenModal(false)
          }
        }}
        selectedRow={selectedRow}
        fieldsToDisplay={modalFields}
      />
    </>
  )
}
